import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { CommonMostLikelyMorphologicCodeResponse } from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { ConfigurationAutomationAssaysModalBasicFormState, initialConfigurationAutomationAssaysModalBasicFormState } from '@/modules/configurationModule/detail/automation/assays/components/assays-modal-basic-form/configuration-automation-assays-modal-basic-form.model'
import { ConfigurationOntologyForAutomaticAssayCreateRequest, ConfigurationOntologyForAutomaticAssayResponse } from '@/modules/configurationModule/detail/automation/assays/services/configuration-ontology-for-automatic-assay/configuration-ontology-for-automatic-assay.dto'
import { configurationOntologyForAutomaticAssayService } from '@/modules/configurationModule/detail/automation/assays/services/configuration-ontology-for-automatic-assay/configuration-ontology-for-automatic-assay.service'
import { Nullable } from '@/shared/types/app.types'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'

@Component({
    components: {
    }
})
export default class ConfigurationAutomationAssaysModalBasicForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly ontologyForAutomaticAssay!: ConfigurationOntologyForAutomaticAssayResponse

    @Emit()
    create(createdAssay: Nullable<ConfigurationOntologyForAutomaticAssayResponse>): Nullable<Partial<ConfigurationOntologyForAutomaticAssayResponse>> {
        return createdAssay
    }

    state: ConfigurationAutomationAssaysModalBasicFormState
    constructor() {
        super()
        this.state = initialConfigurationAutomationAssaysModalBasicFormState()
        this.state.ontologyForAutomaticAssay = this.ontologyForAutomaticAssay
    }

    get updateMode(): boolean {
        return !!this.ontologyForAutomaticAssay?.id
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get isTopographicCodeDisabled(): boolean {
        return !this.state.ontologyForAutomaticAssay.sampleType
    }

    get isProcedureDisabled(): boolean {
        return this.isTopographicCodeDisabled || !this.state.ontologyForAutomaticAssay.topographicCode
    }

    get isMostLikelyMorphologicCodeDisabled(): boolean {
        return this.isProcedureDisabled || !this.state.ontologyForAutomaticAssay.procedure
    }

    /// SERVICES CALLS ///

    async getSampleTypes(): Promise<CommonSampleTypeResponse[]> {
        const response = await this.$_toCallSecure(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getTopographicCodes(): Promise<CommonTopographicCodeResponse[]> {
        const response = await this.$_toCallSecure(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getProcedures(): Promise<CommonProcedureResponse[]> {
        const response = await this.$_toCallSecure(commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getMostLikelyMorphologicCodes(): Promise<CommonMostLikelyMorphologicCodeResponse[]> {
        const response = await this.$_toCallSecure(commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    private async _addOntologiesForAutomaticAssay(
        request: ConfigurationOntologyForAutomaticAssayCreateRequest
    ): Promise<ConfigurationOntologyForAutomaticAssayResponse | null> {
        if (request) {
            const response = await this.$_toCallSecure(configurationOntologyForAutomaticAssayService.create(
                this.state.laboratoryId,
                request
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///
    private _createOperation(): Promise<ConfigurationOntologyForAutomaticAssayResponse | null> {
        return this._addOntologiesForAutomaticAssay(
            {
                sampleTypeId: this.state.ontologyForAutomaticAssay.sampleType!.id,
                topographicCodeId: this.state.ontologyForAutomaticAssay.topographicCode?.id ?? null,
                procedureId: this.state.ontologyForAutomaticAssay.procedure?.id ?? null,
                mostLikelyMorphologicCodeId: this.state.ontologyForAutomaticAssay.mostLikelyMorphologicCode?.id ?? null
            }
        )
    }

    async doAction(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            const assayOperation = await this._createOperation()
            if (assayOperation) {
                // emits an event
                this.create(assayOperation)
                await this.$validator.reset()
            }
        }
    }
}
